import type { EnemyDefinition, HeroDefinition } from '../../shared/game/world-types.js';
import type { SchoolId } from '../../content/spells/schools.js';
import { monsterCastingFolder, voicePackFor, voicePacks } from '../../content/voices/voice-packs.js';
import { MIN_CASTING_VOICE_SECONDS } from '../../content/voices/voice-events.js';
import type { VoicePlacement } from './voice-director.js';
import { spellVoice } from './spell-voice.js';

const files = import.meta.glob('../assets/audio/dialogue/hero voices/*/casting/*.{mp3,wav,flac,ogg}', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;

function clips(hero: HeroDefinition, school?: SchoolId): string[] {
  const folder = voicePacks.find(pack => pack.id === voicePackFor(hero))?.folder;
  return Object.entries(files).filter(([path]) => path.includes(`/hero voices/${folder}/casting/`) && (!school || path.split('/').pop()!.replace(/\.[^.]+$/, '') === school)).map(([, url]) => url);
}

/** Decode incantations before the school effects begin, so playback never waits for a request. */
export function prepareCastingVoice(hero: HeroDefinition) { for (const url of clips(hero)) void spellVoice.preload(url); }

/** The school orb and the spoken incantation begin together. No command acknowledgement or random speech odds. */
export function playCastingVoice(hero: HeroDefinition, school: SchoolId, seconds: number, placement: VoicePlacement | null) {
  if (seconds < MIN_CASTING_VOICE_SECONDS) return;
  const choices = clips(hero, school);
  if (choices.length) spellVoice.play(choices[Math.floor(Math.random() * choices.length)], undefined, placement ?? undefined);
}

const monsterFiles = import.meta.glob('../assets/audio/dialogue/bandit voices/*/casting/*.{mp3,wav,flac,ogg}', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;

function monsterClips(enemy: EnemyDefinition, school?: SchoolId): string[] {
  const folder = monsterCastingFolder(enemy.appearance?.rig);
  return Object.entries(monsterFiles).filter(([path]) => !!folder && path.includes(`/${folder}/casting/`) && (!school || path.split('/').pop()!.replace(/\.[^.]+$/, '') === school)).map(([, url]) => url);
}

export function prepareMonsterCastingVoice(enemy: EnemyDefinition) { for (const url of monsterClips(enemy)) void spellVoice.preload(url); }

/** When each caster's line ends (`performance.now()` ms), so a monster that casts again at once does not talk over itself. */
const speaking = new Map<string, number>();

/**
 * A monster's windup is the 3 s `MONSTER_ACTION_SECONDS`, below the hero threshold, and its line is recorded for that cast, so no minimum applies.
 * The recording can outlast the windup: the bolt leaves while the line finishes.
 */
export function playMonsterCastingVoice(enemy: EnemyDefinition, school: SchoolId, placement: VoicePlacement | null, now = performance.now()) {
  if ((speaking.get(enemy.id) ?? 0) > now) return;
  const choices = monsterClips(enemy, school);
  if (!choices.length) return;
  const seconds = spellVoice.play(choices[Math.floor(Math.random() * choices.length)], undefined, placement ?? undefined);
  if (seconds > 0) speaking.set(enemy.id, now + seconds * 1000);
}
